/**
 * ป้ายชื่อแผงค้ามาตรฐานของทั้งระบบ — รูปแบบ "ชื่อแผง (เลขแผง)"
 *
 * มติลูกค้า 12 ก.ย. 2569: ทุกจุดที่แสดงชื่อแผงค้าต้องมีเลขที่แผงค้ากำกับเสมอ
 * ก่อนหน้านี้มีตรรกะประกอบป้ายเขียนซ้ำกระจายอยู่ 5 ที่ ด้วยตัวคั่น 4 แบบ (`-`, `·`, วงเล็บสองทาง)
 * ทุกจุดต้องเรียกผ่านไฟล์นี้เท่านั้น ห้ามเขียน template literal ประกอบเองในหน้า
 * มิฉะนั้นจะหลุดเคสวงเล็บว่างตอนข้อมูลไม่ครบ
 *
 * ใช้กับ dropdown / หน้ารายละเอียด / popup / เอกสารพิมพ์
 * **ไม่ใช้กับตารางรายการ** ที่แยกคอลัมน์ "ชื่อแผงค้า | เลขแผงค้า" อยู่แล้ว (มติข้อ 2 — คงไว้ 2 คอลัมน์)
 */

/**
 * รูปแบบที่ backend ส่งแผงค้ามา — โมดูลเดิมเป็น camelCase โมดูลใหม่เป็น snake_case
 * รับ `any` ได้ด้วยเพราะหน้าส่วนใหญ่ยังไม่ได้ประกาศ type ของ response
 */
export type StallLike =
  | {
      stallName?: string | null;
      stallNumber?: string | null;
      stall_name?: string | null;
      stall_number?: string | null;
    }
  | null
  | undefined;

/** เลขแผง — normalize ชื่อ field ให้เหลือทางเดียว */
export const stallNumberOf = (stall: StallLike): string | null =>
  stall?.stallNumber ?? stall?.stall_number ?? null;

/** ชื่อแผง — normalize ชื่อ field ให้เหลือทางเดียว */
export const stallNameOf = (stall: StallLike): string | null =>
  stall?.stallName ?? stall?.stall_name ?? null;

/**
 * ป้ายมาตรฐาน "ชื่อแผง (เลขแผง)"
 *
 * | ข้อมูลที่มี | ผลลัพธ์ |
 * |---|---|
 * | ครบทั้งคู่ | `ร้านตี๋ ปูทะเล (2/24)` |
 * | มีแต่ชื่อ | `ร้านตี๋ ปูทะเล` |
 * | มีแต่เลข | `2/24` |
 * | ไม่มีทั้งคู่ | ค่า `dash` |
 *
 * เช็ค `name && number` ก่อนใส่วงเล็บเสมอ — กันเคส `ร้านตี๋ ()` และ `(2/24)`
 * ที่พบจริงเพราะหลายแผงมี stallName เป็นค่าว่าง (ดู addAnnouncerental.tsx)
 */
export const stallLabel = (stall: StallLike, dash = "-"): string => {
  const name = stallNameOf(stall);
  const number = stallNumberOf(stall);
  if (name && number) return `${name} (${number})`;
  return name || number || dash;
};

/** เวอร์ชันรับค่าแยก สำหรับหน้าที่ destructure ชื่อ/เลขออกมาแล้ว */
export const stallLabelOf = (
  stallNumber?: string | null,
  stallName?: string | null,
  dash = "-"
): string => stallLabel({ stallNumber, stallName }, dash);

/**
 * ป้ายแผงค้าจาก object `bidder` — รูปที่พบบ่อยที่สุดในโมดูลสัญญา/โอนสิทธิ์/ยกเลิกสัญญา
 *
 * ⚠️ `bidder` มี `stallName` แบบ denormalize แต่เลขแผงมาได้ 2 ทางแล้วแต่เส้น API:
 * บางเส้นแบนมาเป็น `bidder.stallName` + `bidder.stallNumber` (เช่น GET /bidder)
 * บางเส้นต้องอ่านจาก relation `bidder.stall.stallNumber` (เช่น lease-agreement)
 * จึงลองทั้งสองทาง — ถ้าเส้นไหนไม่ส่งมาเลยจะเหลือเฉพาะชื่อร้าน (ไม่พังและไม่มีวงเล็บว่าง)
 * ดูรายการเส้นที่ยังไม่ยืนยันใน stall-number-checklist.md หัวข้อ 4.1
 */
export const bidderStallLabel = (bidder: any, dash = "-"): string =>
  stallLabel(
    {
      stallName: bidder?.stallName,
      stallNumber: bidder?.stallNumber ?? bidder?.stall?.stallNumber,
    },
    dash
  );

/**
 * ป้ายแผงค้าจาก 1 ใบแจ้งหนี้ (invoice)
 *
 * เส้น list ของ invoice คืน `stallName` / `stallNumber` แบบแบนไว้ที่รากของแถว
 * แต่หน้ารายละเอียด (มาจาก `GET /invoice/:id`) เดิมอ่านผ่าน
 * `leaseAgreement.leaseAgreementApprove.bidder` จึงลองที่รากก่อนแล้วค่อยตกไปที่ bidder
 * เพื่อไม่ต้องรอ backend เพิ่ม join ถ้าเส้น detail ส่ง field แบนมาให้อยู่แล้ว
 */
export const invoiceStallLabel = (invoice: any, dash = "-"): string => {
  const bidder = invoice?.leaseAgreement?.leaseAgreementApprove?.bidder;
  return stallLabel(
    {
      stallName: invoice?.stallName ?? bidder?.stallName,
      stallNumber:
        invoice?.stallNumber ?? bidder?.stallNumber ?? bidder?.stall?.stallNumber,
    },
    dash
  );
};

/**
 * ข้อความสำหรับ `filterOptions` ของ AutocompleteSelect
 *
 * ตัว wrapper ส่งค่านี้เข้า `createFilterOptions({ stringify })` ซึ่งถ้าไม่ส่งจะ fallback
 * ไปใช้ getOptionLabel เอง — ป้ายแบบ ค มีทั้งเลขและชื่ออยู่แล้วจึงค้นได้ทั้งคู่โดยไม่ต้องใช้ตัวนี้
 * ใช้เมื่อ option มี field อื่นที่ต้องค้นเจอด้วย หรือเมื่อ getOptionLabel ไม่ได้แสดงครบ
 */
export const stallSearchText = (stall: StallLike): string =>
  [stallNumberOf(stall), stallNameOf(stall)].filter(Boolean).join(" ");
